import { Box } from "@samdesk/components/Box";
import { Divider } from "@samdesk/components/Divider";
import { Heading } from "@samdesk/components/Heading";
import { Link } from "@samdesk/components/Link";
import { Stack } from "@samdesk/components/Stack";
import { Text } from "@samdesk/components/Text";
import samdeskLogo from "images/samdeskFontLogo.png";
import { withErrorBoundary } from "utils/ErrorBoundary";

const PrivacyPolicy = () => {
	return (
		<div style={scrollWrapperStyle}>
			<Stack direction="column" spacing="none">
				<Box spacingHorizontal="larger" spacingVertical="base">
					<a href="/">
						<img alt="samdesk" src={samdeskLogo} style={logoStyle} />
					</a>
				</Box>
				<Divider />
				<Box spacing="largest" style={{ maxWidth: "800px", margin: "0 auto" }}>
					<Stack direction="column" spacing="large">
						<Heading level="1">Privacy Policy</Heading>
						<Text prominence="secondary">Effective Date: May 2021</Text>
						<Text prominence="secondary">Last Updated: May 2026</Text>

						<Heading level="3">Introduction</Heading>
						<Text>
							Welcome to <Text weight="bold">Samdesk Canada Inc.</Text>
						</Text>
						<Text>
							Samdesk Canada Inc. (“us”, “we”, or “our”) operates our
							application at{" "}
							<Link href="https://dash.samdesk.io">
								https://dash.samdesk.io
							</Link>{" "}
							(hereinafter referred to as <Text weight="bold">“Service”</Text>).
						</Text>
						<Text>
							Our Privacy Policy governs your use of our application hosted at
							dash.samdesk.io, and explains how we collect, safeguard and
							disclose information that results from your use of our Service.
						</Text>
						<Text>
							We use your data to provide and improve the Service. By using the
							Service, you agree to the collection and use of information in
							accordance with this policy. Unless otherwise defined in this
							Privacy Policy, the terms used in this Privacy Policy have the
							same meanings as in our Terms and Conditions.
						</Text>
						<Text>
							Our Terms and Conditions (<Text weight="bold">“Terms”</Text>)
							govern all use of our Service and together with the Privacy Policy
							constitutes your agreement with us (
							<Text weight="bold">“agreement”</Text>).
						</Text>
						<Text>
							<i>
								Please note that this privacy policy does not govern the
								collection and use of information by companies that samdesk does
								not control, nor by individuals not employed or managed by
								samdesk. If you visit a Web site that we mention, link to, or
								provide third party notifications via, be sure to review its
								privacy policy before providing the site with any personal
								information.
							</i>
						</Text>

						<Heading level="3">Definitions</Heading>
						<Text>
							<Text weight="bold">SERVICE</Text> means the The samdesk
							application, accessed at dash.samdesk.io, our mobile application
							downloaded from third party application marketplaces, or accessed
							via our Application Programming Interface (API).
						</Text>
						<Text>
							<Text weight="bold">ACCOUNT DATA</Text> is data required to create
							an account at dash.samdesk.io: email address, and username, both
							of which can be generic and/or anonymous.
						</Text>
						<Text>
							<Text weight="bold">USAGE DATA</Text> is data collected
							automatically either generated by the use of Service or from
							Service infrastructure itself (for example, the duration of a page
							visit).
						</Text>
						<Text>
							<Text weight="bold">COOKIES</Text> are small files stored on your
							device (computer or mobile device).
						</Text>
						<Text>
							<Text weight="bold">DATA CONTROLLER</Text> means a natural or
							legal person who (either alone or jointly or in common with other
							persons) determines the purposes for which and the manner in which
							any data are, or are to be, processed. For the purpose of this
							Privacy Policy, we are a Data Controller of your data.
						</Text>
						<Text>
							<Text weight="bold">DATA PROCESSORS (OR SERVICE PROVIDERS)</Text>{" "}
							means any natural or legal person, or data processing software or
							services, who processes the data on behalf of the Data Controller.
							We may use the services of various Service Providers in order to
							process your data more effectively.
						</Text>
						<Text>
							<Text weight="bold">THE USER</Text> is the individual using our
							Service.
						</Text>

						<Heading level="3">Information Collection and Use</Heading>
						<Text>
							We collect several different types of information for various
							purposes to provide and improve our Service to you.
						</Text>

						<Heading level="3">Types of Data Collected</Heading>
						<Text weight="bold">Account Data</Text>
						<Text>
							While using our Service, we may ask you to provide us with certain
							information that can be used to contact or identify you as a
							specific user of the Software (“Account Data”). Account Data may
							include, but is not limited to:
						</Text>
						<ol>
							<li>
								<Text>Email address</Text>
							</li>
							<li>
								<Text>First name and last name</Text>
							</li>
							<li>
								<Text>Cookies and Usage Data</Text>
							</li>
						</ol>
						<Text>
							We may use your Account Data to contact you with newsletters,
							marketing or promotional materials and other information that may
							be of interest to you. You may opt out of receiving any, or all,
							of these communications from us by following the unsubscribe link.
						</Text>

						<Text weight="bold">Usage Data</Text>
						<Text>
							We may also collect information that your browser sends whenever
							you visit our Service or when you access Service by or through a
							mobile device (<Text weight="bold">“Usage Data”</Text>).
						</Text>
						<Text>
							This Usage Data may include information such as your computer's
							Internet Protocol address (e.g. IP address), browser type, browser
							version, the pages of our Service that you visit, the time and
							date of your visit, the time spent on those pages, unique device
							identifiers and other diagnostic data.
						</Text>
						<Text>
							When you access Service with a mobile device, this Usage Data may
							include information such as the type of mobile device you use,
							your mobile device unique ID, the IP address of your mobile
							device, your mobile operating system, the type of mobile Internet
							browser you use, unique device identifiers and other diagnostic
							data.
						</Text>

						<Text weight="bold">Location Data</Text>
						<Text>
							We may use and store information about your location if you give
							us permission to do so (<Text weight="bold">“Location Data”</Text>
							). We use this data to provide features of our Service, and to
							improve and customize our Service.
						</Text>
						<Text>
							You can enable or disable location services when you use our
							Service at any time by way of your device settings.
						</Text>

						<Text weight="bold">Tracking Cookies Data</Text>
						<Text>
							We use cookies and similar tracking technologies to track the
							activity on our Service and we hold certain information.
						</Text>
						<Text>
							Cookies are files with a small amount of data which may include an
							anonymous unique identifier. Cookies are sent to your browser from
							a website and stored on your device. Other tracking technologies
							are also used such as beacons, tags and scripts to collect and
							track information and to improve and analyze our Service.
						</Text>
						<Text>
							You can instruct your browser to refuse all cookies or to indicate
							when a cookie is being sent. However, if you do not accept
							cookies, you may not be able to use some portions of our Service.
						</Text>
						<Text>Examples of Cookies we use:</Text>
						<ol>
							<li>
								<Text>
									<Text weight="bold">Session Cookies</Text>: We use Session
									Cookies to operate our Service.
								</Text>
							</li>
							<li>
								<Text>
									<Text weight="bold">Preference Cookies</Text>: We use
									Preference Cookies to remember your preferences and various
									settings.
								</Text>
							</li>
							<li>
								<Text>
									<Text weight="bold">Security Cookies</Text>: We use Security
									Cookies for security purposes.
								</Text>
							</li>
						</ol>

						<Heading level="3">Use of Data</Heading>
						<Text>
							Samdesk Canada Inc. uses the collected data for various purposes:
						</Text>
						<ol>
							<li>
								<Text>to provide and maintain our Service;</Text>
							</li>
							<li>
								<Text>to notify you about changes to our Service;</Text>
							</li>
							<li>
								<Text>
									to allow you to participate in interactive features of our
									Service when you choose to do so;
								</Text>
							</li>
							<li>
								<Text>to provide customer support;</Text>
							</li>
							<li>
								<Text>
									to gather analysis or valuable information so that we can
									improve our Service;
								</Text>
							</li>
							<li>
								<Text>to monitor the usage of our Service;</Text>
							</li>
							<li>
								<Text>to detect, prevent and address technical issues;</Text>
							</li>
							<li>
								<Text>
									to fulfill any other purpose for which you provide it;
								</Text>
							</li>
							<li>
								<Text>
									to carry out our obligations and enforce our rights arising
									from any contracts entered into between you and us, including
									for billing and collection;
								</Text>
							</li>
							<li>
								<Text>
									to provide you with notices about your account and/or
									subscription, including expiration and renewal notices,
									email-instructions, etc.;
								</Text>
							</li>
							<li>
								<Text>
									to provide you with news, special offers and general
									information about other goods, services and events which we
									offer that are similar to those that you have already
									purchased or enquired about unless you have opted not to
									receive such information;
								</Text>
							</li>
							<li>
								<Text>
									in any other way we may describe when you provide the
									information;
								</Text>
							</li>
							<li>
								<Text>for any other purpose with your consent.</Text>
							</li>
						</ol>
						<Text>
							We will never share your data or personal information with third
							parties for the purposes of advertising or sell your data to third
							parties.
						</Text>

						<Heading level="3">Retention of Data</Heading>
						<Text>
							We will retain your Account Data only for as long as is necessary
							for the purposes set out in this Privacy Policy. We will retain
							and use your Account Data to the extent necessary to comply with
							our legal obligations (for example, if we are required to retain
							your data to comply with applicable laws), resolve disputes, and
							enforce our legal agreements and policies. If your organization’s
							Order Form and Standard Terms and Conditions Agreement stipulates
							additional restrictions on data retention, we will follow those as
							well.
						</Text>
						<Text>
							We will also retain Usage Data for internal analysis purposes.
							Usage Data is generally retained for a shorter period, except when
							this data is used to strengthen the security or to improve the
							functionality of our Service, or we are legally obligated to
							retain this data for longer time periods.
						</Text>

						<Heading level="3">Transfer of Data</Heading>
						<Text>
							Your information, including Account Data, may be transferred to –
							and maintained on – computers located outside of your state,
							province, country or other governmental jurisdiction where the
							data protection laws may differ from those of your jurisdiction.
						</Text>
						<Text>
							If you are located outside United States and choose to provide
							information to us, please note that we transfer the data,
							including Account Data, to United States and process it there.
						</Text>
						<Text>
							Your consent to this Privacy Policy followed by your submission of
							such information represents your agreement to that transfer.
						</Text>
						<Text>
							Samdesk Canada Inc. will take all the steps reasonably necessary
							to ensure that your data is treated securely and in accordance
							with this Privacy Policy and no transfer of your Account Data will
							take place to an organisation or a country unless there are
							adequate controls in place including the security of your data and
							other personal information.
						</Text>

						<Heading level="3">Disclosure of Data</Heading>
						<Text>We may disclose Data that we collect, or you provide:</Text>
						<ol>
							<li>
								<Text>
									<Text weight="bold">Disclosure for Law Enforcement.</Text>{" "}
									Under certain circumstances, we may be required to disclose
									your Account Data if required to do so by law or in response
									to valid requests by public authorities.
								</Text>
							</li>
							<li>
								<Text>
									<Text weight="bold">Business Transaction.</Text> If we or our
									subsidiaries are involved in a merger, acquisition or asset
									sale, your Account Data may be transferred.
								</Text>
							</li>
							<li>
								<Text>
									<Text weight="bold">
										Other cases. We may disclose your information also:
									</Text>
								</Text>
								<ol>
									<li>
										<Text>to our subsidiaries and affiliates;</Text>
									</li>
									<li>
										<Text>
											to contractors, service providers, and other third parties
											we use to support our business;
										</Text>
									</li>
									<li>
										<Text>
											to fulfill the purpose for which you provide it;
										</Text>
									</li>
									<li>
										<Text>
											for any other purpose disclosed by us when you provide the
											information;
										</Text>
									</li>
									<li>
										<Text>with your consent in any other cases;</Text>
									</li>
								</ol>
							</li>
						</ol>

						<Heading level="3">Security of Data</Heading>
						<Text>
							The security of your data is important to us but remember that no
							method of transmission over the Internet or method of electronic
							storage is 100% secure. While we strive to use commercially
							acceptable means to protect your Account Data, we cannot guarantee
							its absolute security.
						</Text>

						<Heading level="3">
							Your Data Protection Rights Under General Data Protection
							Regulation (GDPR)
						</Heading>
						<Text>
							If you are a resident of the European Union (EU) and European
							Economic Area (EEA), you have certain data protection rights,
							covered by GDPR. – See more at{" "}
							<Link href="https://eur-lex.europa.eu/eli/reg/2016/679/oj">
								https://eur-lex.europa.eu/eli/reg/2016/679/oj
							</Link>
						</Text>
						<Text>
							We aim to take reasonable steps to allow you to correct, amend,
							delete, or limit the use of your Account Data.
						</Text>
						<Text>
							If you wish to be informed what Account Data we hold about you and
							if you want it to be removed from our systems, please email us at{" "}
							<Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link>.
						</Text>
						<Text>
							In certain circumstances, you have the following data protection
							rights:
						</Text>
						<ol>
							<li>
								<Text>
									the right to access, update or to delete the information we
									have on you;
								</Text>
							</li>
							<li>
								<Text>
									the right of rectification. You have the right to have your
									information rectified if that information is inaccurate or
									incomplete;
								</Text>
							</li>
							<li>
								<Text>
									the right to object. You have the right to object to our
									processing of your Account Data;
								</Text>
							</li>
							<li>
								<Text>
									the right of restriction. You have the right to request that
									we restrict the processing of your personal information;
								</Text>
							</li>
							<li>
								<Text>
									the right to data portability. You have the right to be
									provided with a copy of your Account Data in a structured,
									machine-readable and commonly used format;
								</Text>
							</li>
							<li>
								<Text>
									the right to withdraw consent. You also have the right to
									withdraw your consent at any time where we rely on your
									consent to process your personal information;
								</Text>
							</li>
						</ol>
						<Text>
							Please note that we may ask you to verify your identity before
							responding to such requests. Please note, we may not able to
							provide Service without some necessary data.
						</Text>
						<Text>
							You have the right to complain to a Data Protection Authority
							about our collection and use of your Account Data. For more
							information, please contact your local data protection authority
							in the European Economic Area (EEA).
						</Text>

						<Heading level="3">
							Your Data Protection Rights under the California Privacy
							Protection Act (CalOPPA)
						</Heading>
						<Text>
							CalOPPA is the first state law in the nation to require commercial
							websites and online services to post a privacy policy. The law’s
							reach stretches well beyond California to require a person or
							company in the United States (and conceivable the world) that
							operates websites collecting personally identifiable information
							from California consumers to post a conspicuous privacy policy on
							its website stating exactly the information being collected and
							those individuals with whom it is being shared, and to comply with
							this policy. – See more at:{" "}
							<Link href="https://consumercal.org/about-cfc/cfc-education-foundation/california-online-privacy-protection-act-caloppa-3/">
								https://consumercal.org/about-cfc/cfc-education-foundation/california-online-privacy-protection-act-caloppa-3/
							</Link>
						</Text>
						<Text>According to CalOPPA we agree to the following:</Text>
						<ol>
							<li>
								<Text>users can visit our site anonymously;</Text>
							</li>
							<li>
								<Text>
									our Privacy Policy link includes the word “Privacy”, and can
									easily be found on the page specified above on the home page
									of our website;
								</Text>
							</li>
							<li>
								<Text>
									users will be notified of any privacy policy changes on our
									Privacy Policy Page;
								</Text>
							</li>
							<li>
								<Text>
									users are able to change their personal information by
									emailing us at{" "}
									<Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link>.
								</Text>
							</li>
						</ol>
						<Text>Our Policy on “Do Not Track” Signals:</Text>
						<Text>
							We honor Do Not Track signals and do not track, plant cookies, or
							use advertising when a Do Not Track browser mechanism is in place.
							Do Not Track is a preference you can set in your web browser to
							inform websites that you do not want to be tracked.
						</Text>
						<Text>
							You can enable or disable Do Not Track by visiting the Preferences
							or Settings page of your web browser.
						</Text>

						<Heading level="3">
							Your Data Protection Rights under the California Consumer Privacy
							Act (CCPA)
						</Heading>
						<Text>
							If you are a California resident, you are entitled to learn what
							data we collect about you, ask to delete your data and not to sell
							(share) it. To exercise your data protection rights, you can make
							certain requests and ask us:
						</Text>
						<ol>
							<li>
								<Text>
									<Text weight="bold">
										What personal information we have about you.
									</Text>{" "}
									If you make this request, we will return to you:
								</Text>
								<ol>
									<li>
										<Text>
											The categories of personal information we have collected
											about you.
										</Text>
									</li>
									<li>
										<Text>
											The categories of sources from which we collect your
											personal information.
										</Text>
									</li>
									<li>
										<Text>
											The business or commercial purpose for collecting or
											selling your personal information.
										</Text>
									</li>
									<li>
										<Text>
											The categories of third parties with whom we share
											personal information.
										</Text>
									</li>
									<li>
										<Text>
											The specific pieces of personal information we have
											collected about you.
										</Text>
									</li>
									<li>
										<Text>
											A list of categories of personal information that we have
											sold, along with the category of any other company we sold
											it to. If we have not sold your personal information, we
											will inform you of that fact.
										</Text>
									</li>
									<li>
										<Text>
											A list of categories of personal information that we have
											disclosed for a business purpose, along with the category
											of any other company we shared it with.
										</Text>
									</li>
								</ol>
							</li>
						</ol>
						<Text>
							Please note, you are entitled to ask us to provide you with this
							information up to two times in a rolling twelve-month period. When
							you make this request, the information provided may be limited to
							the personal information we collected about you in the previous 12
							months.
						</Text>
						<ol>
							<li>
								<Text>
									To delete your personal information. If you make this request,
									we will delete the personal information we hold about you as
									of the date of your request from our records and direct any
									service providers to do the same. In some cases, deletion may
									be accomplished through de-identification of the information.
									If you choose to delete your personal information, you may not
									be able to use certain functions that require your personal
									information to operate.
								</Text>
							</li>
							<li>
								<Text>
									To stop selling your personal information. We don't sell or
									rent your personal information to any third parties for any
									purpose. You are the only owner of your Account Data and can
									request disclosure or deletion at any time.
								</Text>
							</li>
						</ol>
						<Text>
							Please note, if you ask us to delete or stop selling your data, it
							may impact your experience with us, and you may not be able to
							participate in certain programs or membership services which
							require the usage of your personal information to function. But in
							no circumstances, we will discriminate against you for exercising
							your rights.
						</Text>
						<Text>
							To exercise your California data protection rights described
							above, please send your request(s) by one of the following means:
						</Text>
						<Text>
							By email: <Link href="mailto:dpo@samdesk.io">dpo@samdesk.io</Link>
						</Text>
						<Text>
							Your data protection rights, described above, are covered by the
							CCPA, short for the California Consumer Privacy Act. To find out
							more, visit the official California Legislative Information
							website. The CCPA took effect on 01/01/2020.
						</Text>

						<Heading level="3">Service Providers</Heading>
						<Text>
							We may employ third party companies and individuals to facilitate
							our Service (<Text weight="bold">Service Providers</Text>),
							provide Service on our behalf, perform Service-related services or
							assist us in analysing how our Service is used.
						</Text>
						<Text>
							These third parties have access to your Account Data only to
							perform these tasks on our behalf and are obligated not to
							disclose or use it for any other purpose.
						</Text>

						<Heading level="3">Analytics</Heading>
						<Text>
							We may use third-party Service Providers to monitor and analyze
							the use of our Service.
						</Text>
						<Text weight="bold">Mixpanel</Text>
						<Text>Mixpanel is provided by Mixpanel Inc.</Text>
						<Text>
							You can prevent Mixpanel from using your information for analytics
							purposes by opting-out. To opt-out of Mixpanel service, please
							visit this page:{" "}
							<Link href="https://mixpanel.com/optout/">
								https://mixpanel.com/optout/
							</Link>
						</Text>
						<Text>
							For more information on what type of information Mixpanel
							collects, please visit the Terms of Use page of Mixpanel:{" "}
							<Link href="https://mixpanel.com/terms/">
								https://mixpanel.com/terms/
							</Link>
						</Text>

						<Heading level="3">Links to Other Sites</Heading>
						<Text>
							Our Service may contain links to other sites that are not operated
							by us. If you click a third party link, you will be directed to
							that third party's site. We strongly advise you to review the
							Privacy Policy of every site you visit.
						</Text>
						<Text>
							We have no control over and assume no responsibility for the
							content, privacy policies or practices of any third party sites or
							services.
						</Text>

						<Heading level="3">Children's Privacy</Heading>
						<Text>
							Our Services are not intended for use by children under the age of
							18 (<Text weight="bold">“Child”</Text> or{" "}
							<Text weight="bold">“Children”</Text>).
						</Text>
						<Text>
							We do not knowingly collect information from Children under 18. If
							you become aware that a Child has provided us with Account Data,
							please contact us. If we become aware that we have collected
							Account Data from Children without verification of parental
							consent, we take steps to remove that information from our
							servers.
						</Text>

						<Heading level="3">Changes to This Privacy Policy</Heading>
						<Text>
							We may update this Privacy Policy from time to time. When we make
							material changes, we will notify you by email and/or a prominent
							notice on our Service before the change becomes effective, and we
							will update the "effective date" at the top of this Privacy
							Policy.
						</Text>

						<Text>
							For minor or non-material changes, we may update this Privacy
							Policy without providing separate notice, so we recommend that you
							review this Privacy Policy periodically to stay informed. Changes
							to this Privacy Policy are effective when they are posted on this
							page.
						</Text>

						<Heading level="3">Contact Us</Heading>
						<Text>
							If you have any questions about this Privacy Policy, please
							contact us:
						</Text>
						<Text>
							By email:{" "}
							<Link href="mailto:dpo@samdesk.io">privacy@samdesk.io</Link>
						</Text>
					</Stack>
				</Box>
			</Stack>
		</div>
	);
};

const logoStyle = {
	height: "20px",
	width: "auto",
	display: "block",
};

const scrollWrapperStyle = {
	height: "100vh",
	overflowY: "auto" as const,
};

export default withErrorBoundary(PrivacyPolicy);
